/**
 * @fileoverview EntityRow — a single row inside a ContentCard body. Three
 * slots: leading icon, middle entity-title (title + optional multi-line
 * muted subtitle), trailing actions (typically an "Actions" caret menu
 * button). Used by /settings/authentication for the Passkeys list, and by
 * other control-panel pages that display a list of registered entities
 * (tokens, sessions, devices, etc.).
 *
 * Foundry mapping: `control-panel-internal-components__container` with the
 * inner `bp6-entity-title.bp6-entity-title-heading-h5` block.
 */

import type { ReactNode } from "react";

interface EntityRowProps {
  /** Leading icon — typically a Blueprint <Icon icon="..." size={20} />. */
  icon: ReactNode;
  title: string;
  /** Optional subtitle lines (each rendered as its own muted line). */
  subtitleLines?: ReactNode[];
  /** Trailing actions — typically a Popover-wrapped Button with caret-down. */
  actions?: ReactNode;
  className?: string;
}

export function EntityRow({
  icon,
  title,
  subtitleLines,
  actions,
  className,
}: EntityRowProps) {
  return (
    <div data-testid="entity-row" className={["bp-entity-row", className].filter(Boolean).join(" ")}>
      <div className="bp-entity-row-icon">{icon}</div>
      <div className="bp-entity-row-text">
        <h5 className="bp6-heading bp-entity-row-title">{title}</h5>
        {subtitleLines && subtitleLines.length > 0 && (
          <div className="bp6-text-muted bp-entity-row-subtitle">
            {subtitleLines.map((line, i) => (
              // Subtitle lines are static labels supplied by the parent;
              // index is stable for the lifetime of the row.
              // eslint-disable-next-line react/no-array-index-key
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
      </div>
      {actions !== undefined && <div className="bp-entity-row-actions">{actions}</div>}
    </div>
  );
}
